import { Card, CardContent, Grid2 as Grid, Link, Stack, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/PhoneInTalk';
import EmailIcon from '@mui/icons-material/MailOutline';
import { WhatsappIcon } from '@/components/WhatsappIcon';
import { whatsappLink } from '@/lib/whatsapp';
import { useTranslation } from 'react-i18next';
import { Section } from '@/components/Section';
import { ContactForm } from './ContactForm';
import { setting, usePublicContent } from './use-public-content';
import { useSectionCopy } from './use-section-copy';

export function Contact() {
  const { t } = useTranslation();
  const { settings } = usePublicContent();
  const phone = setting(settings, 'company.phone', '+49 176 62655180');
  const email = setting(settings, 'company.email', 'info@planundvolt.de');
  const chat = whatsappLink(settings['company.whatsapp'], t('contact.whatsappDraft'));
  const copy = useSectionCopy('contact', {
    eyebrow: t('contact.eyebrow'),
    title: t('contact.title'),
    subtitle: t('contact.subtitle'),
  });

  return (
    <Section
      id="kontakt"
      tone="muted"
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle}
    >
      <Grid container spacing={{ xs: 4, md: 6 }}>
        <Grid size={{ xs: 12, md: 4 }} sx={{ width: "100%" }}>
          <Stack spacing={2.5}>
            <ContactCard
              icon={<PhoneIcon />}
              label={t('contact.callUs')}
              value={phone}
              href={`tel:${phone.replace(/\s/g, '')}`}
            />
            <ContactCard
              icon={<WhatsappIcon />}
              label={t('contact.whatsappUs')}
              value={t('contact.whatsappCta')}
              href={chat}
              external
            />
            <ContactCard
              icon={<EmailIcon />}
              label={t('contact.writeUs')}
              value={email}
              href={`mailto:${email}`}
            />
            <Typography variant="body2" color="text.secondary">
              {t('contact.closing')}
            </Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
              <ContactForm />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Section>
  );
}

function ContactCard({
  icon,
  label,
  value,
  href,
  external = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href: string;
  external?: boolean;
}) {
  return (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {icon}
          <Stack>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Link
              href={href}
              variant="h5"
              color="text.primary"
              sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 24 }}
              {...(external ? { target: '_blank', rel: 'noopener' } : {})}
            >
              {value}
            </Link>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
